import React, { useState, useEffect } from 'react';
import FilterBar from './components/FilterBar';
import LeadsTable from './components/LeadsTable';
import { Lead } from '../../../hooks/useLeads';
import { useSidebar } from '../../../context/SidebarContext';
import { useLeads } from '../../../hooks/useLeads';
import apiHandler from '../../../services/apiServices';
import { toast } from 'react-toastify';
import axios from 'axios';
import { generateAndDownloadCSV } from '../../../utils/csvGenerator';
import { useParams } from '@tanstack/react-router';

interface LeadsProps {
  isOpen: boolean;
}

const Leads: React.FC<LeadsProps> = ({ isOpen }) => {
  const [selectedLeads, setSelectedLeads] = useState<Lead[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [sortOption, setSortOption] = useState<string>('created_at__desc');
  const { isSidebarOpen } = useSidebar();
  const { 
    leads, 
    totalLeads, 
    isLoading, 
    fetchNextPage, 
    hasNextPage, 
    isFetchingNextPage 
  } = useLeads(20, sortOption);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isSideDrawerOpen, setIsSideDrawerOpen] = useState(false);

  useEffect(() => {
    const path = window.location.pathname;
    const leadId = path.match(/\/accounts\/(.+)/)?.[1];
    
    if (leadId) {
      const lead = leads.find(l => l._id === leadId);
      if (lead) {
        setSelectedLead(lead);
        setIsSideDrawerOpen(true);
      }
    }
  }, [leads]);

  const handleSort = (option: string) => {
    setSortOption(option);
  };

  const handleDownloadCSV = async () => {
    setIsDownloading(true);
    let leadsToDownload: Lead[] = [];

    try {
      if (selectedLeads.length > 0) {
        leadsToDownload = selectedLeads;
      } else {
        const response = await apiHandler.get('/intent-leads/clubbed/?skip=0&limit=499', {
          timeout: 30000
        });
        
        if (Array.isArray(response.data.data)) {
          leadsToDownload = response.data.data;
        } else {
          toast.error('Invalid data format received');
          return;
        }
      }

      if (leadsToDownload.length === 0) {
        toast.error('No leads available to download');
        return;
      }

      const headers = ['Company Name', 'Intent Signal', 'Categories', 'Website', 'Location'];
      const rows = leadsToDownload.map(lead => [
        lead.company_data.name,
        lead.signals[0]?.signal || '',
        lead.company_data.social_media_company_data[0]?.industries || '',
        lead.company_data.company_website_data?.domain || '',
        lead.company_data.social_media_company_data[0]?.location || ''
      ]);

      generateAndDownloadCSV({
        headers,
        rows,
        filename: 'leads.csv'
      });

    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNABORTED') {
          toast.error('Request timed out. Please try again.');
        } else {
          toast.error('Error downloading leads. Please try again.');
        }
      } else {
        toast.error('An unexpected error occurred');
      }
      console.error('Error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCloseSideDrawer = () => {
    setIsSideDrawerOpen(false);
    window.history.pushState({}, '', '/accounts');
  };

  return (
    <div className={`flex flex-col transition-all duration-300 ${
      isSidebarOpen ? 'ml-[262px]' : 'ml-0'
    }`}>
      <div className="bg-[#FFFFFF]">
        <FilterBar 
          onDownloadCSV={handleDownloadCSV} 
          isDownloading={isDownloading}
          onSortChange={handleSort}
        />
      </div>
      <div className="flex items-center justify-center">
        <LeadsTable 
          selectedLeads={selectedLeads}
          setSelectedLeads={setSelectedLeads}
          sidebarOpen={isOpen}
          leads={leads}
          totalLeads={totalLeads}
          isLoading={isLoading}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          sortOption={sortOption}
          onCloseSideDrawer={handleCloseSideDrawer}
        />
      </div>
    </div>
  );
};

export default Leads; 